/* eslint-disable no-console */
import VertxEventBus from '@vertx/eventbus-bridge-client.js';

// project-specific config
import { BASE_URL } from 'config/main';
import { TENANT } from 'constants/misc';
import type { Handler, Registration } from './EventBusUpdated.types';

const STEP = 5_000;
const MAX = 60_000;

let eb: VertxEventBus | null = null;
let queue: Registration[] = []; // pending until open
let regs: Registration[] = []; // active registrations
let reconnectDelay = 0;
let giveUp = false;
let hasOpened = false; // a later open is a reconnect
let reconnectListeners: Array<() => void> = [];

// helpers
// The backend's address, as the old lib/EventBus does: locally the page comes from the Vite dev
// server while the eventbus lives on the backend (VITE_BASE_URL); a production build has an empty
// BASE_URL and the page's own origin serves both.
const fullUrl = () => {
  const base = BASE_URL || window.location.origin;

  const u = new URL(base);
  u.pathname = u.pathname.replace(/\/$/, '') + '/eventbus';
  return u.toString();
};

const openBus = () => {
  if (eb) return;

  eb = new VertxEventBus(fullUrl(), { vertxbus_ping_interval: 2_000 });

  eb.onopen = () => {
    console.log('[EB] connected', new Date());
    reconnectDelay = 0;

    // Flush queued regs
    queue.splice(0).forEach((r) => {
      eb!.registerHandler(r.address, r.handler);
      regs.push(r);
    });

    // Messages sent while the socket was down are gone for good; tell the subscribers to catch up.
    if (hasOpened) reconnectListeners.forEach((listener) => listener());
    hasOpened = true;
  };

  eb.onclose = (e) => {
    console.warn('[EB] closed', e);
    eb = null;

    // The registrations died with the socket; queue them so the next open attaches them again.
    queue = [...queue, ...regs];
    regs = [];

    if (giveUp) return;

    setTimeout(openBus, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay + STEP, MAX);

    if (reconnectDelay >= MAX) {
      console.error('[EB] giving up after repeated failures');
      giveUp = true;
    }
  };
};

// Public API — compatible with the old Dojo wrapper
function register(address: string, handler: Handler) {
  address = TENANT + address;

  // If we previously gave up, let explicit usage revive the bus
  if (giveUp) {
    giveUp = false;
    reconnectDelay = 0; // start fresh
  }

  // no auto-start at module load
  if (!eb) {
    openBus();
  }

  // If already open, register immediately; otherwise queue it
  if (eb && eb.state === VertxEventBus.OPEN) {
    eb.registerHandler(address, handler);
    regs.push({ address, handler });
    console.log('[EB] handler registered for', address);
  } else {
    queue.push({ address, handler });
  }
}

function unregister(address: string, handler: Handler) {
  address = TENANT + address;
  // The bus is null while reconnecting; a handler registered before it opened is still queued and
  // would otherwise be attached on open, after its owner has gone.
  if (eb?.state === VertxEventBus.OPEN) {
    eb.unregisterHandler(address, handler);
  }
  const isOther = (r: Registration) =>
    r.address !== address || r.handler !== handler;
  queue = queue.filter(isOther);
  regs = regs.filter(isOther);
  console.log('[EB] handler un-registered for', address);
}

/** Calls `listener` whenever the bus reopens after a drop; returns the function that stops it. */
function onReconnect(listener: () => void) {
  reconnectListeners.push(listener);
  return () => {
    reconnectListeners = reconnectListeners.filter((l) => l !== listener);
  };
}

export default {
  register,
  unregister,
  onReconnect,
  getRegistered: () => regs.slice(),
  isRegistered: (addr: string, h: Handler) =>
    regs.some((r) => r.address.includes(addr) && r.handler === h),
  close: () => eb?.close()
};

export const FLEX_TASKS_ADDR = '/secure/users/flexportal/tasks';
export const OPEN_REQUESTS_ADDR = '/roster/open-requests';
